import { useEffect, useMemo, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useFilters } from '@/contexts/FilterContext';
import { useUi } from '@/contexts/UiContext';
import { useBrandCompetitorIds, useBrands } from '@/hooks/useBrands';
import { useBusinessUnits, useRegions } from '@/hooks/useBusinessUnits';
import {
  useCompetitorFilterApplicable,
  useGlobalFiltersApplicable,
} from '@/hooks/useFilterBarApplicable';
import { useMyTeamBrands } from '@/hooks/useTeams';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import {
  MultiSelectDropdown,
  type MultiSelectOption,
} from '@/components/shared/MultiSelectDropdown';
import {
  deriveBrandOptions,
  deriveCompetitorOptions,
  pickDefaultBrandId,
} from '@/utils/teamBrands';
import { CHANNEL_LABEL, CHANNEL_ICON } from '@/utils/channel';
import { CHANNELS, type Channel } from '@/types';

/** "All Channels" sentinel for the channel picker (the context stores `undefined`). */
const ALL_CHANNELS = 'all';

/** Channel picker: All Channels (default) | Email | SMS/MMS — TECH-626 §4.1. */
function ChannelDropdown({
  channel,
  onChange,
}: {
  channel: Channel | undefined;
  onChange: (channel: Channel | undefined) => void;
}) {
  const options: { id: string; label: string; icon: string }[] = [
    { id: ALL_CHANNELS, label: 'All Channels', icon: 'bi-layers' },
    ...CHANNELS.map((c) => ({ id: c, label: CHANNEL_LABEL[c], icon: CHANNEL_ICON[c] })),
  ];
  const selected = options.find((o) => o.id === (channel ?? ALL_CHANNELS)) ?? options[0];

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        className="w-100 text-start"
        aria-label="Channel"
      >
        <i className={`bi ${selected.icon} me-1`} aria-hidden="true" />
        {selected.label}
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100">
        {options.map((opt) => (
          <Dropdown.Item
            key={opt.id}
            active={opt.id === selected.id}
            onClick={() => onChange(opt.id === ALL_CHANNELS ? undefined : (opt.id as Channel))}
          >
            <i className={`bi ${opt.icon} me-2`} aria-hidden="true" />
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function SingleSelectDropdown({
  label,
  icon,
  options,
  selectedId,
  onChange,
  isLoading,
}: {
  label: string;
  icon: string;
  options: MultiSelectOption<number>[];
  selectedId: number | undefined;
  onChange: (id: number | undefined) => void;
  isLoading?: boolean;
}) {
  const selectedLabel = options.find((o) => o.id === selectedId)?.label;

  return (
    <Dropdown>
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100 text-start">
        <i className={`bi ${icon} me-1`} aria-hidden="true" />
        {selectedLabel ?? label}
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100" style={{ maxHeight: 300, overflowY: 'auto' }}>
        {isLoading && <div className="text-muted small px-3 py-1">Loading...</div>}
        {!isLoading && options.length === 0 && (
          <div className="text-muted small px-3 py-1">No options available</div>
        )}
        {options.map((opt) => (
          <Dropdown.Item
            key={opt.id}
            active={opt.id === selectedId}
            onClick={() => onChange(opt.id)}
          >
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

/**
 * Global filter bar: Brand, Business Unit, Region, Date Range, Competitor, Channel.
 * State lives in FilterContext and is passed to every dashboard API call.
 */
export function GlobalFilterBar() {
  const filterBarApplicable = useGlobalFiltersApplicable();
  const competitorFilterApplicable = useCompetitorFilterApplicable();
  const { filterBarVisible } = useUi();
  const {
    brandId,
    businessUnitIds,
    regionIds,
    competitorBrandIds,
    dateRange,
    channel,
    setBrandId,
    setBusinessUnitIds,
    setRegionIds,
    setCompetitorBrandIds,
    setDateRange,
    setChannel,
    resetFilters,
    activeFilterCount,
  } = useFilters();

  const { data: brandData, isLoading: brandsLoading } = useBrands({ pageSize: 200 });
  // Team → brand map for the cascading Brand/Competitors pickers.
  const { teams, isLoading: teamsLoading, unavailable: teamsUnavailable } = useMyTeamBrands();
  // Business units are only listable per-brand on the backend, and the picker
  // is scoped to the currently selected brand — it stays empty until one is chosen.
  const { data: buData, isLoading: busLoading } = useBusinessUnits(brandId);
  const { data: regions, isLoading: regionsLoading } = useRegions();
  // Stored competitor set for the selected brand (admin-managed on the Brands page).
  // Seeds the Competitors picker whenever the brand changes.
  const { data: storedCompetitorIds, isLoading: storedCompetitorsLoading } =
    useBrandCompetitorIds(brandId);

  const allBrandOptions = useMemo(
    () => (brandData?.items ?? []).map((b) => ({ id: b.id, label: b.name, isPrimary: false })),
    [brandData],
  );
  // Cascading pickers driven by team membership:
  //  - Brand: each team's primary brand, plus every brand of teams with no primary.
  //  - Competitors: the other brands in the team(s) the selected brand belongs to.
  // When team data is not available for this user (viewer/analyst, or the
  // endpoint is missing) both pickers fall back to the plain scoped brand list.
  const useTeamCascade = !teamsUnavailable && teams.length > 0;
  const brandOptions = useMemo(
    () => (useTeamCascade ? deriveBrandOptions(teams) : allBrandOptions),
    [useTeamCascade, teams, allBrandOptions],
  );
  const competitorOptions = useMemo(
    () =>
      useTeamCascade
        ? deriveCompetitorOptions(teams, brandId)
        : allBrandOptions.filter((b) => b.id !== brandId),
    [useTeamCascade, teams, brandId, allBrandOptions],
  );
  const pickersLoading = brandsLoading || (!teamsUnavailable && teamsLoading);
  const buOptions = useMemo(
    () => (buData ?? []).map((bu) => ({ id: bu.id, label: bu.name })),
    [buData],
  );
  // Regions are scoped to the selected business units: only regions that
  // belong to at least one currently-selected business unit are offered.
  const regionNameById = useMemo(
    () => new Map((regions ?? []).map((r) => [r.id, r.name])),
    [regions],
  );
  const regionOptions = useMemo(() => {
    const selectedBuIds = new Set(businessUnitIds);
    const ids = new Set(
      (buData ?? []).filter((bu) => selectedBuIds.has(bu.id)).flatMap((bu) => bu.regionIds ?? []),
    );
    return Array.from(ids).map((id) => ({ id, label: regionNameById.get(id) ?? `Region ${id}` }));
  }, [buData, businessUnitIds, regionNameById]);

  // Default to the user's primary brand (first alphabetically, if they belong
  // to more than one team with a primary) on initial load, if none is selected
  // yet; falls back to the first brand in the list when none is primary. Also
  // re-picks if the current brand drops out of the option set (e.g. team data
  // finished loading and narrowed the list to primary brands).
  useEffect(() => {
    if (pickersLoading || brandOptions.length === 0) return;
    if (brandId !== undefined && brandOptions.some((o) => o.id === brandId)) return;
    const defaultBrandId = pickDefaultBrandId(brandOptions);
    if (defaultBrandId !== undefined) setBrandId(defaultBrandId);
  }, [brandId, pickersLoading, brandOptions, setBrandId]);

  // Pre-populate the competitor selection from the brand's stored competitor
  // set. Runs once per brand pick (FilterContext clears the selection when the
  // brand changes) and only while the selection is still empty, so a user who
  // deliberately unticks everything is not overridden. Options still come from
  // team membership: stored competitors outside the user's scope are dropped.
  const seededCompetitorBrandRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (brandId === undefined) {
      seededCompetitorBrandRef.current = undefined;
      return;
    }
    if (seededCompetitorBrandRef.current === brandId) return;
    if (pickersLoading || storedCompetitorsLoading || storedCompetitorIds === undefined) return;
    seededCompetitorBrandRef.current = brandId;
    if (competitorBrandIds.length > 0) return;
    const valid = new Set(competitorOptions.map((o) => o.id));
    const defaults = storedCompetitorIds.filter((id) => id !== brandId && valid.has(id));
    if (defaults.length > 0) setCompetitorBrandIds(defaults);
  }, [
    brandId,
    pickersLoading,
    storedCompetitorsLoading,
    storedCompetitorIds,
    competitorOptions,
    competitorBrandIds,
    setCompetitorBrandIds,
  ]);

  // Keep the competitor selection valid as the option set changes (e.g. team
  // data finishes loading after a brand was already picked).
  useEffect(() => {
    if (pickersLoading || competitorBrandIds.length === 0) return;
    const valid = new Set(competitorOptions.map((o) => o.id));
    const pruned = competitorBrandIds.filter((id) => valid.has(id));
    if (pruned.length !== competitorBrandIds.length) setCompetitorBrandIds(pruned);
  }, [pickersLoading, competitorOptions, competitorBrandIds, setCompetitorBrandIds]);

  // Once a brand is chosen and its business units load, default to selecting
  // all business units and all of their regions (runs once per brand pick).
  // Also keeps the region selection valid as business units are manually
  // changed: drops any region that no longer belongs to a selected business
  // unit, and — since an empty selection reads as "no regions chosen" in the
  // UI even though it filters as "all regions" — re-defaults to every
  // available region rather than leaving the dropdown looking empty.
  // Combined into a single effect: splitting "apply defaults" and "prune"
  // into two effects let them fire in the same commit (e.g. when the new
  // brand's business units were already cached, so busLoading was never
  // true), with the prune effect closing over the pre-update businessUnitIds/
  // regionIds and clobbering the defaults the first effect had just set —
  // which is what was making Regions reset to empty on some brand switches.
  const appliedBrandRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (brandId === undefined) {
      appliedBrandRef.current = undefined;
      return;
    }
    if (busLoading) return;

    if (appliedBrandRef.current !== brandId) {
      appliedBrandRef.current = brandId;
      const allBuIds = (buData ?? []).map((bu) => bu.id);
      const allRegionIds = Array.from(new Set((buData ?? []).flatMap((bu) => bu.regionIds ?? [])));
      setBusinessUnitIds(allBuIds);
      setRegionIds(allRegionIds);
      return;
    }

    if (businessUnitIds.length === 0) {
      if (regionIds.length > 0) setRegionIds([]);
      return;
    }

    const validRegionIds = new Set(regionOptions.map((o) => o.id));
    const pruned = regionIds.filter((id) => validRegionIds.has(id));
    const next = pruned.length > 0 ? pruned : regionOptions.map((o) => o.id);
    const changed = next.length !== regionIds.length || next.some((id) => !regionIds.includes(id));
    if (changed) setRegionIds(next);
  }, [
    brandId,
    busLoading,
    buData,
    businessUnitIds,
    regionIds,
    regionOptions,
    setBusinessUnitIds,
    setRegionIds,
  ]);

  if (!filterBarApplicable) return null;

  return (
    <div
      className={`global-filter-bar-wrapper${filterBarVisible ? '' : ' global-filter-bar-wrapper-hidden'}`}
      aria-hidden={!filterBarVisible}
    >
      <div className="global-filter-bar border-bottom bg-body-tertiary px-3 py-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div style={{ minWidth: 225, flex: "1 1 17%" }}>
            <SingleSelectDropdown
              label="Brand"
              icon="bi-building"
              options={brandOptions}
              selectedId={brandId}
              onChange={setBrandId}
              isLoading={pickersLoading}
            />
          </div>
          <div style={{ minWidth: 225, flex: "1 1 17%" }}>
            <MultiSelectDropdown
              label="Business Units"
              icon="bi-diagram-3"
              options={buOptions}
              selectedIds={businessUnitIds}
              onChange={setBusinessUnitIds}
              isLoading={brandId !== undefined && busLoading}
              emptyMessage={brandId === undefined ? 'Select a brand first' : 'No options available'}
              actionsBox
            />
          </div>
          <div style={{ minWidth: 160, flex: "1 1 12%", display: "none" }}>
            <MultiSelectDropdown
              label="Regions"
              icon="bi-globe-americas"
              options={regionOptions}
              selectedIds={regionIds}
              onChange={setRegionIds}
              isLoading={businessUnitIds.length > 0 && (busLoading || regionsLoading)}
              emptyMessage={
                businessUnitIds.length === 0
                  ? 'Select business units first'
                  : 'No options available'
              }
            />
          </div>
          {competitorFilterApplicable && (
            <div style={{ minWidth: 225, flex: "1 1 17%" }}>
              <MultiSelectDropdown
                label="Competitors"
                icon="bi-shield"
                options={competitorOptions}
                selectedIds={competitorBrandIds}
                onChange={setCompetitorBrandIds}
                isLoading={
                  pickersLoading ||
                  (brandId !== undefined && (teamsLoading || storedCompetitorsLoading))
                }
                emptyMessage={
                  brandId === undefined ? 'Select a brand first' : 'No competitors available'
                }
                actionsBox
              />
            </div>
          )}
          <div style={{ minWidth: 130, flex: "1 1 10%" }}>
            <ChannelDropdown channel={channel} onChange={setChannel} />
          </div>
          <div style={{ minWidth: 285, maxWidth: 425, flex: "1 1 23%" }}>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>
          <div style={{ width: 80 }}>
            <Button
              variant="link"
              size="sm"
              className="text-decoration-none"
              onClick={resetFilters}
              disabled={activeFilterCount === 0}
            >
              <i className="bi bi-x-circle me-1" aria-hidden="true" />
              Reset
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
